import classNames from 'classnames';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import { historyShape } from '../../../shared/shapes';
import {
  buildOpeningUrl,
  buildProviderSchedulingUrl,
  getQueryFromLocation,
} from '../../../shared/utils/urls';

import { UniversalContext } from '../../../_universal';
import ProviderCardFooter from './ProviderCardFooter';
import ProviderCardHeader from './ProviderCardHeader';
import ProviderCardNotAccepting from './ProviderCardNotAccepting';
import ProviderCardOpenings from './ProviderCardOpenings';

import toDate from '../../../shared/utils/toDate';
import style from './ProviderCard.scss';

const ProviderCard = ({ history, className, provider, reasonForVisit, isOnWidget, toWidget }) => {
  const universalContext = React.useContext(UniversalContext);
  const location = history && history.location;
  const query = getQueryFromLocation(location);

  className = classNames(className, style['provider-card']);
  const providerUrl = buildProviderSchedulingUrl(provider.id, {
    location,
    widget: true,
    params: { exp_widget_v2: '' },
  });
  const specialistName = provider.specialties.nodes.length
    ? provider.specialties.nodes[0].specialistName
    : null;
  if (!provider.onlineSchedulingEnabled || !provider.nextOpening) {
    universalContext.trackEvent({
      event: 'Viewed Provider Not Accepting Appointments',
      properties: {
        isOnWidget,
        providerId: provider.id,
        providerDisplay: provider.display,
        reasonForVisitId: query.reasonForVisitId,
        onlineSchedulingEnabled: provider.onlineSchedulingEnabled,
        hasNextOpening: provider.nextOpening != null,
      },
    });
    return (
      <aside className={className} data-qa="provider-card">
        <ProviderCardHeader
          provider={provider}
          providerUrl={providerUrl}
          providerSpecialty={specialistName}
        />
        <ProviderCardNotAccepting
          display={provider.display}
          reasonForVisitName={reasonForVisit.name}
          phoneNumber={provider.office.phone}
          onlineSchedulingEnabled={provider.onlineSchedulingEnabled}
        />
      </aside>
    );
  }
  const startingDate = toDate(provider.nextOpening.time);
  const nextAvailableUrl = buildOpeningUrl(provider.id, provider.nextOpening.id, {
    location,
    widget: true,
  });
  return (
    <aside className={className} data-qa="provider-card">
      <ProviderCardHeader
        provider={provider}
        providerUrl={providerUrl}
        providerSpecialty={specialistName}
      />
      <ProviderCardOpenings
        providerUrl={providerUrl}
        nextAvailableUrl={nextAvailableUrl}
        startingDate={startingDate}
        timeZone={provider.office.timeZoneCode}
      />
      <ProviderCardFooter providerUrl={providerUrl} isOnWidget={isOnWidget} toWidget={toWidget} />
    </aside>
  );
};

ProviderCard.propTypes = {
  history: historyShape,
  className: PropTypes.string,
  provider: PropTypes.shape({
    id: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
    onlineSchedulingEnabled: PropTypes.bool.isRequired,
    profile: PropTypes.shape({
      photo: PropTypes.string.isRequired,
    }),
    office: PropTypes.shape({
      phone: PropTypes.string.isRequired,
      timeZoneCode: PropTypes.string,
    }),
    specialties: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          specialistName: PropTypes.string.isRequired,
        })
      ).isRequired,
    }).isRequired,
    nextOpening: PropTypes.shape({
      id: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    }),
  }).isRequired,
  reasonForVisit: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  isOnWidget: PropTypes.bool,
  toWidget: PropTypes.bool,
};

ProviderCard.fragment = {
  name: ProviderCard.displayName,
  query: gql`
  fragment ${ProviderCard.displayName} on Provider {
    id
    display
    onlineSchedulingEnabled
    office {
      id
      phone
      timeZoneCode
    }
    profile {
      id
      photo(size: 120)
    }
    specialties(perPage: 1) {
      nodes {
        id
        specialistName
      }
    }
    nextOpening(reasonForVisitId: $reasonForVisitId, after: $after) {
      id
      time
    }
  }
`,
};

export default withRouter(ProviderCard);
